<template>
  <v-container class="pa-4 pt-md-12 page-content">
    <v-row>
      <!-- Desktop layout -->
      <template v-if="isDesktop && !isExtensionPopup">
        <v-col cols="12">
          <h1 class="text-h5 mb-2 font-weight-semibold">
            <span>{{ $t('page.bookmarks.all.title') }}</span>
          </h1>

          <h3 v-if="itemCount !== null && !loading.items && !loading.pinnedItems" class="text-body-2 text-neutral600">
            <span v-if="!hasActiveFilters"> {{ $tc('common.items_n', itemCount) }}</span>
            <span v-if="hasActiveFilters">
              {{
                $tc(
                  'common.items_n_filtered',
                  itemCount,
                  { filteredItemCount, itemCount }
                )
              }}
            </span>
          </h3>
          <v-skeleton-loader v-else max-width="300" type="text" />
        </v-col>
      </template>

      <!-- Cross platform filter buttons -->
      <v-col v-if="!isBulkActionsActive" cols="12">
        <v-row>
          <v-col :cols="(isMobile || isExtensionPopup) && searchFocused ? 12 : undefined">
            <SearchButtonInput
              v-model="searchValue"
              :loading="loading.items || loading.pinnedItems"
              :placeholder="$tc('cta.search_item', 1)"
              :minified="display.xs.value"
              @focused="searchFocused = $event"
            />
          </v-col>

          <template v-if="!searchFocused || !(isMobile || isExtensionPopup)">
            <v-spacer v-if="!(isMobile || isExtensionPopup) || !itemFilters.search" />

            <v-col cols="auto" class="filter-buttons" :class="isExtensionPopup && 'is-extension-popup'">
              <BookmarkFilterSwitcher class="mr-2" @click="onOpenFilter" />
              <BookmarksSortingSwitcher :sorting="sorting" class="mr-2" @set="setSorting" />
              <BookmarksStyleSwitcher :list-style="listStyle" class="mr-2" @set="setListStyle" />
              <BookmarkSelectSwitcher @click="toggleBulkActions" />
            </v-col>
          </template>
        </v-row>
      </v-col>

      <BookmarkBulkActions v-if="isBulkActionsActive" :options="bulkActionOptions" />

      <v-col v-if="isDesktop && !isExtensionPopup" cols="12">
        <v-divider class="my-2" color="neutral50" />
      </v-col>

      <v-col v-if="!loading.items && !loading.pinnedItems && !items.length" cols="12" class="pt-4 pt-md-8">
        <BookmarkNoResults :filters-active="filtersActive" @reset="resetFilters()" />
      </v-col>

      <v-col v-else cols="12" class="bookmarks">
        <CollectionItemList
          :loading="loading.items || loading.pinnedItems"
          :items="items"
          :user-id="userId"
          :list-style="listStyle"
          @action="onItemAction"
          @filter-tags="addFilterTag"
        />
      </v-col>

      <v-col v-if="itemNext" cols="12" class="text-center">
        <BrandButton
          variant="secondary"
          size="x-large"
          class="px-12"
          :loading="loading.items || loading.pinnedItems"
          @click="refreshItems()"
        >
          {{ $t('cta.load_more') }}
        </BrandButton>
      </v-col>
    </v-row>

    <BookmarkFilterIndicator
      v-if="isMobile || isExtensionPopup"
      :model-value="!isFilterSidebarOpen && filtersActive"
      @click="onOpenFilter"
    />
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useHead } from '@unhead/vue'
import {
  PhStar,
  PhTrash,
  PhTagSimple,
  PhListPlus,
} from '@phosphor-icons/vue'
import bus from '@/plugins/eventbus'
import errorHandler from '@/plugins/error-handler'
import toast from '@/plugins/toasts'

// Components
import BookmarkNoResults from './components/BookmarkNoResults.vue'
import SearchButtonInput from '@/components/forms/fields/SearchButtonInput.vue'
import BookmarkFilterSwitcher from './components/BookmarkFilterSwitcher.vue'
import BookmarksSortingSwitcher from './components/BookmarksSortingSwitcher.vue'
import BookmarksStyleSwitcher from './components/BookmarksStyleSwitcher.vue'
import BookmarkSelectSwitcher from './components/BookmarkSelectSwitcher.vue'
import BookmarkBulkActions from './components/BookmarkBulkActions.vue'
import CollectionItemList from './components/CollectionItemList.vue'
import BookmarkFilterIndicator from './components/BookmarkFilterIndicator.vue'

// Composables
import { useCollectionItemView } from '@/composables/useCollectionItemView'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import { usePullToRefresh } from '@/composables/usePullToRefresh'
import { usePinActions } from '@/composables/usePinActions'

// Constants
import { BACK_TARGET_QUERY, ResourceType } from '@/utils/constants'

// Setup
const { t } = useI18n()
const store = useStore()
const router = useRouter()
const route = useRoute()
const display = useDisplay()
const { ptrInit } = usePullToRefresh()

useHead({
  title: computed(() => t('page.bookmarks.all.title')),
})

const searchFocused = ref(false)

// Use the collection item view composable
const {
  // State
  loading,

  // Computed properties
  userId,
  listStyle,
  isExtensionPopup,
  isDesktop,
  isMobile,
  itemNext,
  itemFilters,
  sorting,
  itemCount,
  filteredItemCount,
  hasActiveFilters,
  items,
  filtersActive,
  searchValue,
  isFilterSidebarOpen,

  // Methods
  refreshItems,
  loadItems,
  handleScroll,
  onGetStats,
  setListStyle,
  onOpenFilter,
  resetFilters,
  addFilterTag,
  setSorting,
  ptrOnRefresh,

  // Lifecycle
  initializeCollectionItemView,
  cleanupCollectionItemView,
} = useCollectionItemView()

const { handlePinEdit } = usePinActions({
  refreshItems,
  getPinPath: (item) => `/bookmarks/${item.id}/pin`,
})

// Use the bulk actions V2 composable
const {
  isBulkActionsActive,
  toggleBulkActions,
  bulkToggleItemSelection,
  addBulkToFavorites,
  removeBulkFromFavorites,
  addBulkToCollections,
  bulkAddTags,
  bulkRemoveTags,
  deleteBulkSelected,
} = useBulkActionsV2()

// Computed: Bulk Action Options
const bulkActionOptions = computed(() => [
  {
    text: 'bulk.actions.add_to_favorites',
    icon: PhStar,
    color: 'text-neutral900',
    action: addBulkToFavorites,
    weight: 'fill',
  },
  {
    text: 'bulk.actions.remove_from_favorites',
    icon: PhStar,
    color: 'text-neutral900',
    action: removeBulkFromFavorites,
  },
  {
    text: 'bulk.actions.add_to_collections',
    icon: PhListPlus,
    color: 'text-neutral900',
    action: addBulkToCollections,
  },
  {
    text: 'bulk.actions.add_tags',
    icon: PhTagSimple,
    color: 'text-neutral900',
    action: bulkAddTags,
    weight: 'fill',
  },
  {
    text: 'bulk.actions.remove_tags',
    icon: PhTagSimple,
    color: 'text-neutral900',
    action: bulkRemoveTags,
  },
  {
    text: 'bulk.actions.delete_items',
    icon: PhTrash,
    color: 'text-red400',
    action: deleteBulkSelected,
  },
])

// Get Vuex actions using useStore - uses collectionItem store module
const toggleFavorite = item => store.dispatch('collectionItem/toggleFavorite', item)

// Methods
const onItemAction = (item, { action }) => {
  const isBookmark = item.resourceType === ResourceType.BOOKMARK
  const isNote = item.resourceType === ResourceType.NOTE

  switch (action) {
    case 'favorite':
      toggleFavorite(item).catch(err => errorHandler.toast(err))
      break
    case 'add-to-collection':
      if (isBookmark) {
        router.push(`/bookmarks/${item.id}/add-collections`)
      } else if (isNote) {
        router.push(`/notes/${item.id}/add-collections`)
      }
      break
    case 'tags': {
      const tagsPath = isBookmark
        ? `/bookmarks/${item.id}/tags`
        : isNote ? `/notes/${item.id}/tags` : null
      if (tagsPath) {
        router.push({
          path: tagsPath,
          query: { [BACK_TARGET_QUERY]: route.fullPath },
        })
      }
      break
    }
    case 'comments':
      if (isBookmark) {
        router.push(`/bookmarks/${item.id}`)
      } else if (isNote) {
        router.push(`/notes/${item.id}`)
      }
      break
    case 'pin-edit':
      handlePinEdit(item)
      break
    case 'toggle-select':
      bulkToggleItemSelection(item)
      break
    default:
  }
}

const onNavDuped = () => {
  loadItems()
}

// Lifecycle hooks
onMounted(async () => {
  await initializeCollectionItemView()

  if (isMobile.value) {
    ptrInit({
      onRefresh: ptrOnRefresh,
    })
  }

  // Check for pending imports and show toast if needed
  import('@/services/import-toast-manager').then(({ default: importToastManager }) => {
    importToastManager.checkPendingImport()
  })

  bus.on('router:error:duplicated', onNavDuped)
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  cleanupCollectionItemView()

  bus.off('router:error:duplicated', onNavDuped)
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss" scoped>
.page-content {
  padding-bottom: 50vh !important;
}

.filter-buttons.is-extension-popup {
  :deep(.v-btn .d-sm-inline) {
    display: none !important;
  }
}

.active-filters-notice {
  z-index: 2 !important;
}
</style>
